import { useRef, useState, useEffect, FC, ChangeEvent } from 'react';

import { AirportSearchViewModel } from '@api/index';
import { SearchInput } from '@components/ui/search/SearchBarInput/SearchBarSearchInput/SearchBarSearchInputStyle';
import ResponsiveSearchDrawerItem from '@components/views/components/listing/drawer/ResponsiveSearchDrawerItem';
import { FlightSearchInputDrawerType } from '@components/views/flight/types';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import ResponsiveSearchInputDrawer from '@components/views/components/listing/drawer/ResponsiveSearchInputDrawer/ResponsiveSearchInputDrawer';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import { SeturIcon } from '@components/utils/CustomIcon';
import { useDebounceValue } from '@hooks/hooks';

import AirportSearchResultList from './AirportSearchResultList';

const ResponsiveAirportSearchDrawer: FC<
  {
    onAirportSelect: (airport: AirportSearchViewModel) => void;
  } & FlightSearchInputDrawerType
> = ({ onAirportSelect, title, subTitle, placeholder, drawerTitle, icon }) => {
  return (
    <ResponsiveSearchInputDrawer
      icon={icon}
      title={title}
      subTitle={subTitle}
      placeholder={placeholder}
      drawerTitle={drawerTitle}
      inputComponent='setur'
    >
      {(hideModal) => (
        <ResponsiveAirportSearch
          onAirportSelect={onAirportSelect}
          hideModal={hideModal}
          title={title as string}
          icon={icon}
        />
      )}
    </ResponsiveSearchInputDrawer>
  );
};

const ResponsiveAirportSearch: FC<{
  onAirportSelect: (airport: AirportSearchViewModel) => void;
  title: string;
  icon: SeturIcon;
  hideModal: () => void;
}> = ({ onAirportSelect, title, hideModal, icon }) => {
  const [airportSearchText, setAirportSearchText] = useState('');
  const debouncedAirportSearchText = useDebounceValue(airportSearchText);
  const [selectedAirport, setSelectedAirport] =
    useState<AirportSearchViewModel>();
  const [showList, setShowList] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onSubmit = () => {
    if (selectedAirport) {
      onAirportSelect(selectedAirport);
      hideModal();
    } else {
      setHasError(true);
    }
  };

  const onChangeAirportSearchText = (e: ChangeEvent<HTMLInputElement>) => {
    setAirportSearchText(e.target.value);
    setShowList(true);
    if (!e.target.value) {
      setSelectedAirport(undefined);
    }
  };

  const onChangeAirport = (item: AirportSearchViewModel) => {
    setAirportSearchText(item.airportName + ', ' + item.airportCode);
    setSelectedAirport(item);
    setShowList(false);
    setHasError(false);
  };

  const onFocusChange = () => {
    setShowList(true);
  };

  return (
    <>
      <LocationAnimationErrorBox hasError={hasError}>
        <ResponsiveSearchDrawerItem
          icon={icon}
          title={title}
          placeholder='Havalimanı veya şehir'
          subTitle={
            <SearchInput
              ref={inputRef as any}
              aria-label='search-input'
              placeholder='Havalimanı veya şehir'
              value={airportSearchText}
              onFocus={onFocusChange}
              onChange={onChangeAirportSearchText}
              bordered={false}
            />
          }
        />
      </LocationAnimationErrorBox>
      {showList ? (
        <AirportSearchResultList
          searchText={debouncedAirportSearchText}
          onSelect={onChangeAirport}
        />
      ) : null}
      <ResponsiveApplyButton onClick={onSubmit} />
    </>
  );
};

export default ResponsiveAirportSearchDrawer;
